import React, { useState } from "react";
import { Briefcase, Users, MapPin, Award, Send, FileText, Check, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { PageHero } from "./PageHero";
import { uploadFile, submitCareer } from "../lib/api";

const inputCls = "rounded-none h-12 bg-background border-border focus-visible:border-primary focus-visible:ring-primary";
const triggerCls = "rounded-none h-12 bg-background border-border focus:border-primary focus:ring-primary";

const openPositions = [
  {
    id: 1,
    title: "Project Manager — Demolition",
    location: "DMV Area",
    type: "Full-time",
    description: "Lead demolition projects from planning through completion. Coordinate with contractors, manage timelines, and ensure safety compliance.",
    requirements: ["5+ years project management experience", "Construction or demolition background preferred", "Strong communication and leadership skills", "Knowledge of safety regulations", "Willingness to travel nationwide"],
  },
  {
    id: 2,
    title: "Site Supervisor",
    location: "DMV Area + Travel",
    type: "Full-time",
    description: "Oversee on-site demolition operations, manage crew safety, and coordinate with project managers and clients.",
    requirements: ["3+ years construction/demolition experience", "OSHA certification preferred", "Leadership and problem-solving skills", "Physical ability to work in demanding conditions", "Valid driver's license and reliable transportation"],
  },
  {
    id: 3,
    title: "Heavy Equipment Operator",
    location: "DMV Area + Travel",
    type: "Full-time",
    description: "Operate heavy equipment on active structural demolition and razing sites. Safely and efficiently bring down structures while working closely with the site crew to keep every job on schedule.",
    requirements: ["5+ years of heavy equipment operating experience", "Proven structural razing / demolition experience", "Able to run multiple machines — excavator and loader required", "High-reach excavator experience a strong plus", "Strong understanding of site safety and demolition best practices", "Physical ability to work in demanding conditions", "Willingness to travel for projects"],
  },
  {
    id: 4,
    title: "Demolition Laborer",
    location: "DMV Area + Travel",
    type: "Full-time",
    description: "Perform hands-on demolition and site work as part of the ground crew — assisting with structural teardowns, debris removal, cleanup, and keeping the job site safe and organized.",
    requirements: ["Prior demolition or construction labor experience a plus", "Ability to safely operate hand and power tools", "Physical ability to lift heavy materials and work in demanding conditions", "Strong work ethic and reliability", "Willingness to learn and follow safety protocols", "Valid driver's license and reliable transportation"],
  },
];

const benefits = [
  { icon: <Award className="h-6 w-6" />, title: "Veteran-Friendly", description: "Join a team that values military experience and discipline." },
  { icon: <MapPin className="h-6 w-6" />, title: "Multi-State Projects", description: "Work on diverse projects across multiple states." },
  { icon: <Users className="h-6 w-6" />, title: "Growth Opportunities", description: "Advance your career in a growing company." },
  { icon: <Briefcase className="h-6 w-6" />, title: "Competitive Benefits", description: "Health insurance, retirement plans, and paid time off." },
];

export const CareersPage = () => {
  const [data, setData] = useState({
    name: "", email: "", phone: "", position: "", experience: "", availableStart: "", coverLetter: "",
  });
  const [resume, setResume] = useState(null); // {url, filename}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => setData((prev) => ({ ...prev, [field]: value }));

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadFile(file);
      setResume({ url: res.url, filename: res.filename });
    } catch (err) {
      toast.error("Resume upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email || !data.coverLetter) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await submitCareer({ ...data, resume });
      toast.success("Application submitted! Ben will review it and get back to you soon.");
      setData({ name: "", email: "", phone: "", position: "", experience: "", availableStart: "", coverLetter: "" });
      setResume(null);
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = (title) => {
    setData((prev) => ({ ...prev, position: title }));
    document.getElementById("application-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div data-testid="careers-page">
      <PageHero
        kicker="Careers"
        title="Join Our"
        highlight="Elite Team"
        subtitle="Build your career with a veteran-owned demolition company that values precision, safety, and excellence. Work on challenging projects across multiple states."
      />

      {/* Open positions */}
      <section className="tex-plate py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="label-kicker mb-4">Now Hiring</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white leading-none mb-12">Open Positions</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border border border-border">
            {openPositions.map((p) => (
              <div key={p.id} data-testid={`career-position-${p.id}`} className="bg-card p-8 flex flex-col">
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="bg-primary text-background text-xs font-bold uppercase tracking-wider px-3 py-1">{p.type}</span>
                  <span className="border border-border text-gray-300 text-xs font-semibold uppercase tracking-wider px-3 py-1 flex items-center gap-1"><MapPin className="h-3 w-3" />{p.location}</span>
                </div>
                <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white mb-3">{p.title}</h3>
                <p className="text-gray-400 leading-relaxed mb-5">{p.description}</p>
                <ul className="space-y-2 mb-6">
                  {p.requirements.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                      <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" /> {r}
                    </li>
                  ))}
                </ul>
                <Button data-testid={`career-apply-btn-${p.id}`} onClick={() => scrollToForm(p.title)} className="mt-auto rounded-none font-bold uppercase tracking-widest w-full">
                  Apply Now
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 tex-grid opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="label-kicker mb-4">Why Work With Us</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white leading-none mb-12">The Robo Demo Standard</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
            {benefits.map((b) => (
              <div key={b.title} className="bg-card p-8">
                <div className="w-14 h-14 flex items-center justify-center border border-primary/30 bg-primary/10 text-primary mb-5">{b.icon}</div>
                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white mb-2">{b.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="application-form" className="tex-plate py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-industrial p-8 md:p-10">
            <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white mb-8 flex items-center gap-3">
              <Send className="h-7 w-7 text-primary" /> Submit Your Application
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="career-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="label-kicker mb-2 block">Full Name *</Label>
                  <Input data-testid="career-name-input" value={data.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="John Smith" className={inputCls} required />
                </div>
                <div>
                  <Label className="label-kicker mb-2 block">Email *</Label>
                  <Input data-testid="career-email-input" type="email" value={data.email} onChange={(e) => handleChange("email", e.target.value)} placeholder="you@email.com" className={inputCls} required />
                </div>
                <div>
                  <Label className="label-kicker mb-2 block">Phone</Label>
                  <Input data-testid="career-phone-input" type="tel" value={data.phone} onChange={(e) => handleChange("phone", e.target.value)} placeholder="(703) 000-0000" className={inputCls} />
                </div>
                <div>
                  <Label className="label-kicker mb-2 block">Position of Interest</Label>
                  <Select value={data.position} onValueChange={(v) => handleChange("position", v)}>
                    <SelectTrigger data-testid="career-position-select" className={triggerCls}><SelectValue placeholder="Select position" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Project Manager — Demolition">Project Manager — Demolition</SelectItem>
                      <SelectItem value="Site Supervisor">Site Supervisor</SelectItem>
                      <SelectItem value="Heavy Equipment Operator">Heavy Equipment Operator</SelectItem>
                      <SelectItem value="Demolition Laborer">Demolition Laborer</SelectItem>
                      <SelectItem value="General Interest">General Interest</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-kicker mb-2 block">Years of Experience</Label>
                  <Select value={data.experience} onValueChange={(v) => handleChange("experience", v)}>
                    <SelectTrigger data-testid="career-experience-select" className={triggerCls}><SelectValue placeholder="Select experience" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entry">Entry Level (0-2 years)</SelectItem>
                      <SelectItem value="mid">Mid Level (3-5 years)</SelectItem>
                      <SelectItem value="senior">Senior Level (6-10 years)</SelectItem>
                      <SelectItem value="expert">Expert Level (10+ years)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-kicker mb-2 block">Available Start Date</Label>
                  <Input data-testid="career-start-input" type="date" value={data.availableStart} onChange={(e) => handleChange("availableStart", e.target.value)} className={`${inputCls} [color-scheme:dark]`} />
                </div>
              </div>

              <div>
                <Label className="label-kicker mb-2 block">Cover Letter / Why work with us? *</Label>
                <Textarea data-testid="career-cover-input" value={data.coverLetter} onChange={(e) => handleChange("coverLetter", e.target.value)} placeholder="Tell us about your background and why you're interested in Robo Demo LLC..." rows={6} className="rounded-none bg-background border-border focus-visible:border-primary focus-visible:ring-primary" required />
              </div>

              <div>
                <Label className="label-kicker mb-2 block">Resume (Optional)</Label>
                <label htmlFor="resume-upload" data-testid="career-resume-dropzone" className="flex flex-col items-center justify-center border border-dashed border-border bg-background py-8 cursor-pointer hover:border-primary transition-colors">
                  <input id="resume-upload" type="file" accept=".pdf,.doc,.docx" onChange={handleResumeUpload} className="hidden" />
                  {uploading ? <Loader2 className="h-8 w-8 text-primary animate-spin mb-2" /> : <FileText className="h-8 w-8 text-primary mb-2" />}
                  <p className="text-sm text-gray-300">{uploading ? "Uploading..." : "Click to upload resume"}</p>
                  <p className="text-xs text-muted-foreground mt-1">PDF, DOC, DOCX up to 10MB</p>
                </label>
                {resume && (
                  <div className="mt-3 flex items-center justify-between border border-primary/30 bg-primary/10 px-4 py-3" data-testid="career-resume-chip">
                    <span className="text-sm text-white font-medium truncate">{resume.filename}</span>
                    <button type="button" onClick={() => setResume(null)} className="text-gray-300 hover:text-destructive"><X className="h-4 w-4" /></button>
                  </div>
                )}
              </div>

              <div className="flex justify-center pt-2">
                <Button data-testid="career-submit-btn" type="submit" size="lg" disabled={submitting || uploading} className="rounded-none font-bold uppercase tracking-widest px-12 py-6">
                  {submitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting...</>) : (<><Send className="mr-2 h-5 w-5" /> Submit Application</>)}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">Applications are reviewed promptly by our team.</p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};
